import mixpanel from 'mixpanel-browser';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { datadogRum } from '@datadog/browser-rum';
import { useLocation } from '@tanstack/react-router';

import { getTeamMemberDisplayName } from '../utils/nameUtils';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';

declare global {
    interface Window {
        mixpanel: typeof mixpanel;
        enableLogEvents?: () => void;
        disableLogEvents?: () => void;
    }
}

export type EventBody = {
    [key: string]: unknown;
};

export type TrackEventOptions = {
    sendToDataDog?: boolean;
    sendToMixpanel?: boolean;
};

export const AnalyticsContext = createContext({
    baseEventBody: {} as EventBody,
    trackEvent: (
        _eventName: string,
        _eventBody: EventBody = {},
        _options: TrackEventOptions = {}
    ) => {},
    trackPageView: (_pageName: string, _eventBody: EventBody = {}) => {},
});

const IS_DISABLED = !import.meta.env.PROD || !import.meta.env.VITE_MIXPANEL_TOKEN;

interface AnalyticsProviderProps {
    children: React.ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
    const { pathname } = useLocation();
    const { user: auth0User } = useAuth0();

    const { currentUser } = useTeamMemberData();

    const [shouldLogEvents, setShouldLogEvents] = useState(false);

    useEffect(() => {
        mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN);
        window.mixpanel = mixpanel;

        if (IS_DISABLED) {
            mixpanel.disable();
        }

        if (window) {
            window.enableLogEvents = () => {
                console.info('Event logging enabled');
                setShouldLogEvents(true);
            };
            window.disableLogEvents = () => {
                console.info('Event logging disabled');
                setShouldLogEvents(false);
            };
        }

        return () => {
            if (window) {
                window.enableLogEvents = undefined;
                window.disableLogEvents = undefined;
            }
        };
    }, []);

    useEffect(() => {
        if (auth0User && currentUser) {
            try {
                datadogRum?.setUser({
                    id: auth0User?.sub,
                    name: `${auth0User?.given_name ?? ''} ${auth0User?.family_name ?? ''}`,
                    email: currentUser?.email,
                    personRid: currentUser?.personRid,
                    teamMemberRid: currentUser?.teamMemberRid,
                    teamRole: currentUser?.role,
                });
            } catch (e) {
                console.error('Unable to attach user to datadog:', e);
            }
        }
    }, [auth0User, currentUser]);

    const baseEventBody = useMemo(
        () => ({
            personRid: currentUser?.personRid,
            personName: getTeamMemberDisplayName(currentUser),
            teamMemberRid: currentUser?.teamMemberRid,
            teamRole: currentUser?.role,
            pathname,
        }),
        [currentUser, pathname]
    );

    const trackDatadogAction = (actionName: string, payload: EventBody) => {
        try {
            datadogRum?.addAction(actionName, payload);
        } catch (error) {
            console.error('Error tracking datadog action:', error);
        }
    };

    const trackMixpanelEvent = (eventName: string, payload: EventBody) => {
        try {
            mixpanel?.track?.(eventName, payload);
        } catch (error) {
            console.error('Error tracking mixpanel event:', error);
        }
    };

    const trackEvent = useCallback(
        (eventName: string, eventBody: EventBody = {}, options: TrackEventOptions = {}) => {
            const fullEventBody = { ...baseEventBody, ...eventBody };

            const { sendToMixpanel = true, sendToDataDog = true } = options;

            if (IS_DISABLED) {
                return;
            }

            if (shouldLogEvents) {
                console.info('\nEvent:', eventName);
                console.table(fullEventBody);
            }

            if (!eventName) {
                console.warn('No event name provided');
            } else {
                if (sendToDataDog) trackDatadogAction(eventName, eventBody);
                if (sendToMixpanel) trackMixpanelEvent(eventName, fullEventBody);
            }
        },
        [baseEventBody, shouldLogEvents]
    );

    const trackPageView = useCallback(
        (pageName: string, additionalProperties: EventBody = {}) => {
            trackEvent('Page Viewed', {
                ...additionalProperties,
                pageName,
            });
        },
        [trackEvent]
    );

    const contextValues = useMemo(
        () => ({ baseEventBody, trackEvent, trackPageView }),
        [baseEventBody, trackEvent, trackPageView]
    );

    return <AnalyticsContext.Provider value={contextValues}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics() {
    const context = useContext(AnalyticsContext);
    if (context === undefined) {
        throw new Error('useAnalytics must be used within an AnalyticsProvider');
    }
    return context;
}
